import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, combineLatest, map, of } from 'rxjs';
import { EVENTOS_PUBLIC_ID } from 'src/app/core/config/endpoints';
import { isFeatureEnabled } from 'src/app/core/config/features';
import { replaceEdpointParams } from 'src/app/helpers/endpoints-helpers';
import { HttpService } from 'src/app/services/http/http.service';
import { PermissaoAdminEventoService } from 'src/app/services/permissao-admin-evento.service';
import { SessionService } from 'src/app/services/session-service/session.service';
import { EventoAbility, processEventoAbility } from '../eventos.ability';
import { CheckinAbilityHandoff } from './checkin-ability-handoff';

/**
 * Duas checagens INDEPENDENTES, nesta ordem:
 *
 *  1. a tela está ligada?  → feature flag (`checkinsV2`)
 *  2. este usuário pode?   → autorização (CASL, `read Check-ins` no evento)
 *
 * Elas não se substituem. Com a flag desligada ninguém entra, nem admin. Com a
 * flag ligada, quem não tem a ability continua barrado. Usar CASL para fazer o
 * papel da flag (ou vice-versa) é exatamente o atalho que faz a regra sair de
 * sincronia depois.
 *
 * Flag desligada devolve a rota antiga (a aba dentro de meus-eventos/:id), que
 * segue funcionando — não é um erro, é o estado normal enquanto não liberamos.
 */
export const checkinGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const idEvento = route.paramMap.get('idEvento');
  if (!idEvento) return router.createUrlTree(['/private/meus-eventos']);

  // 1. habilitação
  if (!isFeatureEnabled('checkinsV2')) {
    return router.createUrlTree(['/private/meus-eventos', idEvento]);
  }

  // 2. autorização
  const http = inject(HttpService);
  const session = inject(SessionService);
  const permissoes = inject(PermissaoAdminEventoService);
  const ability = inject(EventoAbility);
  const handoff = inject(CheckinAbilityHandoff);

  return combineLatest([
    http.get(replaceEdpointParams(EVENTOS_PUBLIC_ID, idEvento)).pipe(catchError(() => of(null))),
    permissoes.getPermissoesEfetivas(idEvento, session.user.id ?? ""),
    permissoes.getConfiguracaoAdmin(idEvento).pipe(catchError(() => of(null))),
  ]).pipe(
    map(([evento, perms, config]) => processEventoAbility(perms, evento, config, session)),
    map((resolvida) => {
      // Publica as regras na instância global de Ability, como o
      // `eventos-insert-edit` faz. Sem isto o `AbilityService.ability$` nunca
      // emite nesta rota e a sidebar renderiza sem nenhum item — o cálculo
      // ficaria preso dentro do guard.
      ability.update(resolvida.rules);
      // E entrega as mesmas regras para a tela republicar no `ngOnInit`: a rota
      // anterior só é destruída DEPOIS daqui, e o destroy dela zera a ability.
      // Ver CheckinAbilityHandoff.
      handoff.guardar(resolvida.rules);
      return resolvida.can('read', 'Check-ins', idEvento) ? true : router.createUrlTree(['/']);
    }),
    catchError(() => of(router.createUrlTree(['/']))),
  );
};
